import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  applyActionCode,
  confirmPasswordReset,
  verifyPasswordResetCode,
  checkActionCode
} from "firebase/auth";
import { auth } from "../firebase";
import { Button } from "../design/button";
import { Divider } from "../design/divider";
import { FieldGroup, Fieldset, Label } from "../design/fieldset";
import { Heading } from "../design/heading";
import { Input } from "../design/input";
import { Field } from "@headlessui/react";
import { Text } from "../design/text";
import { Link } from "../design/link";
import logo from "../assets/logo.png";

type ActionMode = "resetPassword" | "verifyEmail" | "recoverEmail" | null;

const AuthPageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-zinc-900 dark:via-zinc-800 dark:to-zinc-900">
    <div className="w-full max-w-lg px-4">
      {children}
    </div>
  </div>
);

const AuthCard: React.FC<{ children: React.ReactNode; as?: "form"; onSubmit?: (e: React.FormEvent) => void }> = ({ children, as, onSubmit }) => {
  const className = "w-full p-8 bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:bg-zinc-900/80 dark:border-zinc-700/50";

  if (as === "form") {
    return <form method="post" className={className} onSubmit={onSubmit}>{children}</form>;
  }
  return <div className={className}>{children}</div>;
};

const AuthActionHandler: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const mode = searchParams.get("mode") as ActionMode;
  const actionCode = searchParams.get("oobCode");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState<string>("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const verifyActionCode = async () => {
      if (!actionCode) {
        setError("Invalid or missing action code");
        setLoading(false);
        return;
      }

      try {
        await checkActionCode(auth, actionCode);

        if (mode === "resetPassword") {
          const verifiedEmail = await verifyPasswordResetCode(auth, actionCode);
          setEmail(verifiedEmail);
        } else if (mode === "verifyEmail") {
          await applyActionCode(auth, actionCode);
          setSuccess(true);
        }

        setLoading(false);
      } catch (err: any) {
        console.error("Error verifying action code:", err);
        setError(err.message || "Invalid or expired action code");
        setLoading(false);
      }
    };

    verifyActionCode();
  }, [actionCode, mode]);

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    if (!actionCode) {
      setError("Missing action code");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await confirmPasswordReset(auth, actionCode, newPassword);
      setSuccess(true);
    } catch (err: any) {
      console.error("Error resetting password:", err);
      setError(err.message || "Failed to reset password. The link may have expired.");
    }

    setIsSubmitting(false);
  };

  // Loading
  if (loading) {
    return (
      <AuthPageWrapper>
        <AuthCard>
          <img src={logo} alt="Logo" className="w-24 h-24 mx-auto" />
          <Heading className="mt-6 text-2xl font-bold text-center text-zinc-900 dark:text-white">
            Verifying...
          </Heading>
        </AuthCard>
      </AuthPageWrapper>
    );
  }

  // Error with no mode
  if (error && !mode) {
    return (
      <AuthPageWrapper>
        <AuthCard>
          <img src={logo} alt="Logo" className="w-24 h-24 mx-auto" />
          <Fieldset>
            <Heading className="text-2xl font-bold text-center text-zinc-900 dark:text-white">
              Error
            </Heading>
            <Divider className="my-6" />
            <Text className="text-center text-red-600 dark:text-red-400">{error}</Text>
            <Divider className="my-6" />
            <Button onClick={() => navigate("/login")} className="w-full">
              Back to Login
            </Button>
          </Fieldset>
        </AuthCard>
      </AuthPageWrapper>
    );
  }

  // Password Reset — Success
  if (mode === "resetPassword" && success) {
    return (
      <AuthPageWrapper>
        <AuthCard>
          <img src={logo} alt="Logo" className="w-24 h-24 mx-auto" />
          <Fieldset>
            <Heading className="text-2xl font-bold text-center text-zinc-900 dark:text-white">
              Password Reset Successful
            </Heading>
            <Divider className="my-6" />
            <Text className="text-center text-zinc-600 dark:text-zinc-400">
              Your password has been successfully reset. You can now log in with your new password.
            </Text>
            <Divider className="my-6" />
            <Button onClick={() => navigate("/login")} className="w-full">
              Go to Login
            </Button>
          </Fieldset>
        </AuthCard>
      </AuthPageWrapper>
    );
  }

  // Password Reset — Form
  if (mode === "resetPassword") {
    return (
      <AuthPageWrapper>
        <AuthCard as="form" onSubmit={handlePasswordReset}>
          <img src={logo} alt="Logo" className="w-24 h-24 mx-auto" />
          <Fieldset>
            <Heading className="text-2xl font-bold text-center text-zinc-900 dark:text-white">
              Reset Password
            </Heading>
            <Divider className="my-6" />

            {email && (
              <Text className="mb-4 text-center text-zinc-600 dark:text-zinc-400">
                Resetting password for: <strong className="text-zinc-900 dark:text-white">{email}</strong>
              </Text>
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg dark:bg-red-900/30 dark:border-red-800/50">
                <Text className="text-red-600 dark:text-red-400 text-sm">{error}</Text>
              </div>
            )}

            <FieldGroup>
              <Field>
                <Label htmlFor="newPassword" className="text-sm font-medium text-zinc-700 dark:text-zinc-300">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  minLength={6}
                />
              </Field>
              <Field>
                <Label htmlFor="confirmPassword" className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  minLength={6}
                />
              </Field>
            </FieldGroup>

            <Divider className="my-6" />

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? "Resetting Password..." : "Reset Password"}
            </Button>

            <div className="my-6 text-center">
              <Text className="text-zinc-600 dark:text-zinc-400">
                <Link href="/login" className="text-blue-600 dark:text-blue-400 hover:underline">
                  Back to Login
                </Link>
              </Text>
            </div>
          </Fieldset>
        </AuthCard>
      </AuthPageWrapper>
    );
  }

  // Email Verification
  if (mode === "verifyEmail") {
    return (
      <AuthPageWrapper>
        <AuthCard>
          <img src={logo} alt="Logo" className="w-24 h-24 mx-auto" />
          <Fieldset>
            <Heading className="text-2xl font-bold text-center text-zinc-900 dark:text-white">
              {success ? "Email Verified!" : "Verification Failed"}
            </Heading>
            <Divider className="my-6" />
            <Text className="text-center text-zinc-600 dark:text-zinc-400">
              {success
                ? "Your email has been successfully verified. You can now access all features of your account."
                : error || "Unable to verify email. The link may have expired."}
            </Text>
            <Divider className="my-6" />
            <Button onClick={() => navigate("/login")} className="w-full">
              Go to Login
            </Button>
          </Fieldset>
        </AuthCard>
      </AuthPageWrapper>
    );
  }

  // Recover Email
  if (mode === "recoverEmail") {
    return (
      <AuthPageWrapper>
        <AuthCard>
          <img src={logo} alt="Logo" className="w-24 h-24 mx-auto" />
          <Fieldset>
            <Heading className="text-2xl font-bold text-center text-zinc-900 dark:text-white">
              Email Recovery
            </Heading>
            <Divider className="my-6" />
            <Text className="text-center text-zinc-600 dark:text-zinc-400">
              Email recovery is not yet implemented. Please contact support for assistance.
            </Text>
            <Divider className="my-6" />
            <Button onClick={() => navigate("/login")} className="w-full">
              Back to Login
            </Button>
          </Fieldset>
        </AuthCard>
      </AuthPageWrapper>
    );
  }

  // Unknown mode
  return (
    <AuthPageWrapper>
      <AuthCard>
        <img src={logo} alt="Logo" className="w-24 h-24 mx-auto" />
        <Fieldset>
          <Heading className="text-2xl font-bold text-center text-zinc-900 dark:text-white">
            Invalid Action
          </Heading>
          <Divider className="my-6" />
          <Text className="text-center text-zinc-600 dark:text-zinc-400">
            The action you're trying to perform is not recognized.
          </Text>
          <Divider className="my-6" />
          <Button onClick={() => navigate("/login")} className="w-full">
            Back to Login
          </Button>
        </Fieldset>
      </AuthCard>
    </AuthPageWrapper>
  );
};

export default AuthActionHandler;
